import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, HardHat, Rocket, ArrowLeft, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { LangSwitch } from "@/components/LangSwitch";

export default function RegisterPage() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [form, setForm] = useState({ azienda: "", nome: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  if (user) return <Navigate to="/home" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.azienda.trim() || !form.nome.trim()) return setError(t("fieldRequired"));
    const em = form.email.trim();
    if (!em) return setError(t("emailRequired"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return setError(t("emailInvalid"));
    if ((form.password || "").length < 6) return setError(t("pwdMin"));
    setLoading(true);
    try {
      await api.post("/auth/register-company", form);
      await refresh();
      navigate("/home", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-12 w-12 rounded-md bg-[#0044FF] grid place-items-center shadow-lg shadow-[#0044FF]/30">
            <HardHat className="h-7 w-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black text-2xl tracking-tight">IL-Report</div>
            <div className="text-sm text-slate-500">{t("tagline")}</div>
          </div>
          <LangSwitch />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <h1 className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6 text-[#0044FF]" /> {t("regTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">{t("regSub")}</p>

          {error && (
            <div
              className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <Label className="text-xs font-bold">{t("regAzienda")}</Label>
              <Input
                value={form.azienda}
                onChange={set("azienda")}
                placeholder={t("regAziendaPh")}
                className="h-12 mt-1"
                required
                data-testid="register-azienda-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("regNome")}</Label>
              <Input
                value={form.nome}
                onChange={set("nome")}
                placeholder={t("regNomePh")}
                className="h-12 mt-1"
                required
                data-testid="register-nome-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("email", { ns: "common" })} *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="nome@azienda.it"
                className="h-12 mt-1"
                required
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("regPassword")}</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="••••••••"
                  className="h-12 mt-1 pr-12"
                  minLength={6}
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="register-toggle-password-visibility"
                  aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-14 text-base font-bold bg-[#0044FF] hover:bg-[#0033CC]"
            >
              {loading ? t("regLoading") : (
                <>
                  <Rocket className="h-5 w-5 mr-2" /> {t("regSubmit")}
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-slate-500 underline hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="register-back-to-login"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t("regBack")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
